<template>
  <div class="rf-category-pg-wrapper prompt-flag-two-enabled">
    <RfModalLoading :showDialog="loading" />
    <RfSegmentTrafficModal ref="trafficModal" :segment="segment" />
    <div>
      <div class="all-segments-list-rightcol">
        <div v-if="segment" class="rf-detail-content-wrapper segment-detail-pg">
          <div class="promo-detail-pg-title-btns">
            <div class="promo-dtlpg-title-left">
              <RfSegmentName :item="segment" :locked="segment.is_locked" />
            </div>
            <div class="promo-dtlpg-title-right">
              <RfDetailHeader
                :locked="segment.is_locked"
                :showEnableDisable="shouldShowEnableDisable"
                :item="segment"
                @enableItem="enableSegment"
                @submitDelete="submitDelete"
                @clone="clone"
              />
            </div>
          </div>
          <RfLiveStatistics
            v-model:currChartSrc="currChartSrc"
            v-model:dateRange="dateRange"
            :usage_type="'users'"
            :metrics="metrics"
            :model="segment"
          />
          <div class="rf-segment-detail-sub-content">
            <v-row>
              <v-col class="rf-sub-col left-col">
                <RfUserHistory
                  class="bg-white-1 border-beige-10 rounded-sm border border-solid"
                  :activities="segmentStore.activities"
                  auditableType="Segment"
                  :auditableId="segment.id"
                />
              </v-col>
              <v-col class="rf-sub-col right-col">
                <RfSegmentStatisticsTable
                  v-if="!isPush"
                  class="bg-white-1 border-beige-10 rounded-sm border border-solid"
                  :metrics="metrics && metrics['users']"
                />
                <div class="segment-updated-time">
                  Last updated at
                  {{ dayjs(segment.updated_at).format("MMM Do YYYY, h:mm a") }}
                </div>
              </v-col>
            </v-row>
          </div>
          <div
            v-if="segment.paths.length || segment.sequences.length"
            class="prompts-list--wrapper mt-5"
          >
            <div class="flex items-center justify-between">
              <h3 class="text-h6">
                Active In the following
                <template v-if="segment.paths.length"> Prompts </template>
              </h3>
              <v-btn
                class="mr-2"
                variant="text"
                color="primary"
                @click="
                  exportMultiplePromptToCsv(
                    $refs.segmentPrompts.map(el => el.prompt),
                    currChartSrc,
                  )
                "
              >
                Export All to CSV
              </v-btn>
            </div>
            <RfTable
              v-if="segment.paths.length && promptTable"
              :tableData="promptTable"
              class="[&_.rf-table\-\-footer]:mt-0! [&_table]:bg-transparent!"
              :loading="loading"
              hideTotal
              noIntersection
              @loadMore="() => (promptTable.page += 1)"
            >
              <template #tbody>
                <RfSegmentPrompt
                  v-for="prompt in segment.paths.slice(0, promptTable.showing)"
                  ref="segmentPrompts"
                  :key="prompt.id"
                  class="mt-5 hover:shadow"
                  :promptId="prompt.id"
                  :segment="segment"
                  :currChartSrc="currChartSrc"
                  :dateRange="dateRange"
                  :openAssignTrafficModal="$refs.trafficModal?.show"
                  :disableAssignTraffic="disableAssignTraffic"
                />
              </template>
            </RfTable>

            <template v-if="segment.sequences.length">
              <h4 class="text-h6 mt-3">Guides</h4>
              <RfTable
                v-if="segment.sequences.length && guideTable"
                :tableData="guideTable"
                class="[&_.rf-table\-\-footer]:mt-5! [&_table]:bg-transparent!"
                :loading="loading"
                noIntersection
                hideTotal
                @loadMore="() => (guideTable.page += 1)"
              >
                <template #tbody>
                  <RfSegmentSequence
                    v-for="sequence in segment.sequences.slice(0, guideTable.showing)"
                    :key="sequence.id"
                    class="mt-5 hover:shadow"
                    :sequenceId="sequence.id"
                    :segment="segment"
                    :currChartSrc="currChartSrc"
                    :dateRange="dateRange"
                    :openAssignTrafficModal="$refs.trafficModal?.show"
                    :disableAssignTraffic="disableAssignTraffic"
                  />
                </template>
              </RfTable>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { cloneDeep } from "lodash-es";
import RfUserHistory from "@/components/RfUserHistory.vue";
import RfLiveStatistics from "@/components/RfCommonCards/RfLiveStatistics.vue";
import RfSegmentName from "@/components/RfSegments/RfSegmentName.vue";
import RfSegmentStatisticsTable from "@/components/RfCommonCards/RfSegmentStatisticsTable.vue";
import RfDetailHeader from "@/components/RfDetailHeader.vue";
import RfModalLoading from "@/views/RfModalLoading.vue";
import RfSegmentTrafficModal from "@/blocks/RfModals/RfSegmentTrafficModal.vue";
import dayjs from "dayjs";
import RfSegmentPrompt from "@/components/RfSegments/RfSegmentPrompt.vue";
import RfSegmentSequence from "@/components/RfSegments/RfSegmentSequence.vue";
import { useToastsStore } from "@/pinia/toastsStore";
import { exportMultiplePromptToCsv } from "@/utils/CsvUtils";
import { useLegacyLastErrorStore } from "@/pinia/legacyLastErrorStore";
import { useAppsStore } from "@/pinia/appsStore";
import { useSegmentStore } from "@/pinia/segmentStore";
import RfTable from "@/blocks/RfTable/RfTable.vue";
import { useTableData } from "@/pinia/piniaUtils";
import { ref } from "vue";
import { useDynamicTitle } from "@/utils/composables/useDynamicTitle";

export default {
  name: "RfSegmentView",
  components: {
    RfUserHistory,
    RfLiveStatistics,
    RfSegmentName,
    RfSegmentStatisticsTable,
    RfDetailHeader,
    RfModalLoading,
    RfSegmentPrompt,
    RfSegmentSequence,
    RfSegmentTrafficModal,
    RfTable,
  },
  setup: () => ({
    dayjs,
    toastsStore: useToastsStore(),
    exportMultiplePromptToCsv,
    appsStore: useAppsStore(),
    segmentStore: useSegmentStore(),
    legacyLastErrorStore: useLegacyLastErrorStore(),
    promptTable: ref(),
    guideTable: ref(),
  }),
  data() {
    return {
      deleting: false,
      timerId: null,
      loading: false,
      currChartSrc: null,
      dateRange: null,
    };
  },
  computed: {
    lastError() {
      return this.legacyLastErrorStore.lastError;
    },
    metrics() {
      return this.segmentStore.metrics;
    },
    segment() {
      return this.segmentStore.segment;
    },
    app() {
      return this.appsStore.app;
    },
    isPush() {
      return this.segment.segment_type === "pinpoint";
    },
    shouldShowEnableDisable() {
      return !/^system_/.test(this.segment.segment_type) && !this.isPush;
    },
    disableAssignTraffic() {
      // Traffic splits depend on segment calculation, which default prompts on the
      // All Users and Anonymous Users segments bypass (RE-1445).
      return (
        ["system_all_users", "system_anonymous_users"].includes(this.segment?.segment_type) &&
        "Traffic splits aren't available for this segment"
      );
    },
  },
  watch: {
    async $route() {
      this.clearTimer();
      this.loadSegment();
    },
    segment() {
      if (this.deleting) {
        this.toastsStore.create({ type: "success", body: "Segment deleted successfully" });
        this.timerId = setTimeout(() => {
          this.$router.replace({
            path: `/apps/${this.$route.params.aid}/segments`,
          });
        }, 1000);
        this.loading = false;
        this.deleting = false;
      }
    },
    async lastError(to) {
      if (!to) return;
      if (to.cause === 404) {
        this.$router.push({
          path: `/apps/${this.$route.params.aid}/segments`,
        });
      } else {
        this.clearTimer();
        this.toastsStore.create({ type: "error", body: to.message });
      }
    },
  },
  mounted() {
    useDynamicTitle(() => this.segment?.name && `${this.segment.name} | Segments`);

    this.loadSegment();
  },
  watch: {
    async $route() {
      this.clearTimer();
      this.loadSegment();
    },
    segment() {
      if (this.deleting) {
        this.toastsStore.create({ type: "success", body: "Segment deleted successfully" });
        this.timerId = setTimeout(() => {
          this.$router.replace({
            path: `/apps/${this.$route.params.aid}/segments`,
          });
        }, 1000);
        this.loading = false;
        this.deleting = false;
      }
    },
    async lastError(to) {
      if (!to) return;
      if (to.cause === 404) {
        this.$router.push({
          path: `/apps/${this.$route.params.aid}/segments`,
        });
      } else {
        this.clearTimer();
        this.toastsStore.create({ type: "error", body: to.message });
      }
    },
  },
  mounted() {
    useDynamicTitle(() => this.segment?.name && `${this.segment.name} | Segments`);

    this.loadSegment();
  },
  methods: {
    submitDelete() {
      this.deleting = true;
      this.loading = true;
      this.segmentStore.remove({
        appId: this.$route.params.aid,
        segId: this.$route.params.sid,
      });
    },
    clearTimer() {
      if (this.timerId) {
        clearInterval(this.timerId);
      }
    },
    async loadSegment() {
      const appId = this.$route.params.aid;
      const segId = this.$route.params.sid;
      if (segId && this.segment?.id !== segId) {
        await this.segmentStore.get({ appId, segId });
        const { table: promptTable } = useTableData({
          perPageInit: 5,
          totalItems: this.segment.paths.length,
        });
        const { table: guideTable } = useTableData({
          perPageInit: 5,
          totalItems: this.segment.sequences.length,
        });
        this.promptTable = promptTable;
        this.guideTable = guideTable;
      }
    },
    async enableSegment(enable) {
      this.loading = true;
      const newSegment = cloneDeep(this.segment);
      newSegment.is_enabled = enable;
      await this.segmentStore
        .update({
          appId: this.$route.params.aid,
          segId: newSegment.id,
          modelSegment: newSegment,
        })
        .then(() => (this.loading = false))
        .catch(() => null);
    },
    async clone() {
      const segment = cloneDeep(this.segment);
      segment.id = null;
      segment.is_locked = false;
      segment.name = `${segment.name} copy`;

      await this.segmentStore
        .create({
          appId: this.$route.params.aid,
          modelSegment: segment,
        })
        .then(() => {
          this.toastsStore.create({ type: "success", body: "Segment created successfully" });
          const path = `/apps/${this.$route.params.aid}/segments/${this.segment.id}`;
          this.$router.push({ path });
        })
        .catch(() => null);
    },
  },
};
</script>
